import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import SchoolMissionsTab from "./SchoolMissionsTab.js";
import SchoolPrayerRequestsTab from "./SchoolPrayerRequestsTab.js";

function SchoolView() {
    const [school, setSchool] = useState({
        name: "",
        location_description: "",
        patron_name: "",
        patron_number: "",
        google_map_pin: "",
        description: "",
        population: "",
        school_type: "",
        boarding_type: "",
        bible_study_classes: false,
    });
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [activeTab, setActiveTab] = useState("details");
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']));
    };

    useEffect(() => {
        const fetchSchool = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/schools/${id}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setSchool(data);
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "School not found." });
                    } else if (response.status === 401) {
                        navigate("/logout", { state: { from: location } });
                    } else {
                        throw new Error("Failed to fetch school.");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the school." });
                }
            }
        };

        const fetchData = async () => {
            await fetchSchool();
            setLoading(false);
        };

        fetchData();
    }, [id, navigate, location]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <main className="school-view">
            {loading ? (
                <div className="has-text-centered py-5">
                    <span className="icon is-large">
                        <i className="fas fa-spinner fa-pulse fa-2x" />
                    </span>
                    <p>Loading schools...</p>
                </div>
            ) : (

                <div className="school-header">
                    <div className="school-header-content">
                        <div>
                            <h1 className="school-title">{school.name}</h1>
                            <p className="school-subtitle">
                                {school.school_type} • {school.county?.name || "Unknown County"}
                            </p>
                        </div>
                        <div className="school-actions">
                            <Link to="/dashboard/schools" className="button is-light">
                                <i className="fas fa-arrow-left"></i> &nbsp; Back
                            </Link>
                            {canEdit(userRole) && (
                                <Link to={`/dashboard/schools/${school.id}/update`} className="button is-primary">
                                    <i className="fas fa-edit"></i> Edit
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            <div className="tabs is-centered">
                <ul>
                    <li className={activeTab === "details" ? "is-active" : ""}>
                        <Link onClick={() => setActiveTab("details")}>
                            <i className="fa-solid fa-graduation-cap mr-2"></i> School Details
                        </Link>
                    </li>
                    <li className={activeTab === "missions" ? "is-active" : ""}>
                        <Link onClick={() => setActiveTab("missions")}>
                            <i className="fas fa-calendar-alt mr-2"></i> All Missions
                        </Link>
                    </li>
                    <li className={activeTab === "prayer-requests" ? "is-active" : ""}>
                        <Link onClick={() => setActiveTab("prayer-requests")}>
                            <i className="fas fa-praying-hands mr-2"></i> Prayer Requests
                        </Link>
                    </li>
                </ul>
            </div>

            <div className="school-content">
                {activeTab === "details" && (
                    <div className="columns">
                        <div className="column is-12">
                            <div className="card">
                                <div className="card-content">
                                    <h3 className="title is-4">About {school.name}</h3>
                                    <div className="content">
                                        {school.description ? (
                                            <p className="preserve-whitespace">{school.description}</p>
                                        ) : (
                                            <p className="has-text-grey">No description provided</p>
                                        )}
                                    </div>

                                    <div className="detailed-info-grid mt-5">
                                        <div className="info-item">
                                            <h4>School Type</h4>
                                            <p>{school.school_type || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Boarding Type</h4>
                                            <p>{school.boarding_type || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Patron Name</h4>
                                            <p>{school.patron_name || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Patron Contact</h4>
                                            <p>{school.patron_number || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Population</h4>
                                            <p>{school.population || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Bible Study Classes?</h4>
                                            <p>{school.bible_study_classes ? "Yes" : "No"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>New Believers Classes?</h4>
                                            <p>{school.new_believers_classes ? "Yes" : "No"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Prayer Meetings?</h4>
                                            <p>{school.prayer_meetings ? "Yes" : "No"}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mt-4">
                                <div className="card-content">
                                    <h3 className="title is-4">Location Information</h3>
                                    <div className="detailed-info-grid">
                                        <div className="info-item">
                                            <h4>County</h4>
                                            <p>{school.county?.name || "N/A"}</p>
                                        </div>
                                        <div className="info-item">
                                            <h4>Google Map Pin</h4>
                                            {school.google_map_pin ? (
                                                <Link to={school.google_map_pin} target="_blank" rel="noopener noreferrer">
                                                    View on Map
                                                </Link>
                                            ) : (
                                                <p>N/A</p>
                                            )}
                                        </div>
                                        <div className="info-item full-width">
                                            <h4>Location Description</h4>
                                            <p>{school.location_description || "N/A"}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mt-4">
                                <div className="card-content">
                                    <h3 className="title is-4">Prayer Needs</h3>
                                    <div className="content">
                                        {school.needs ? (
                                            <p className="preserve-whitespace">{school.needs}</p>
                                        ) : (
                                            <p className="has-text-grey">No prayer needs recorded</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {activeTab === "missions" && <SchoolMissionsTab />}
                {activeTab === "prayer-requests" && <SchoolPrayerRequestsTab />}
            </div>
        </main>
    );
}

export default SchoolView;